import {ChangeEvent, useEffect, useMemo, useRef, useState} from 'react'
import {ClipboardCheck, ImagePlus, Trash2, UploadCloud, X} from 'lucide-react'
import {Select} from 'antd'
import actionTypes from '@constants/actionTypes'
import ErrorToast from 'components/modal/Alert/ErrorToast'
import SuccessToast from 'components/modal/Alert/SuccessToast'
import ModalLayout from 'components/modal/ModalLayout'
import Spinner from 'components/spinner/Spinner'
import useDispatchAction from '@hooks/useDispatchAction'
import cn from '@utils/cn'
import {
  ApiAlignerCheckInPayload,
  IAlignerCheckInPhotoName,
  postAlignerCheckIn,
} from 'redux/Slices/AppSlice/LeadsProfile/AlignerTracking.slice'
import {ActionItem} from 'screens/Patients/LeadsProfile/leadsProfile.types'
import {Aligner} from '../patientTimeline.types'
import {generateRandomAlphanumeric, safeParseInt} from 'utils/ConstFunctions'
import type {AllTreatmentPlanListItem} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'

type JawTypeLabel = 'Upper' | 'Lower' | 'Both'

type AlignerCheckInOption = {
  key: string
  label: string
  alignerNo: number
  jawType: JawTypeLabel
  alignerJourneyId?: number
  totalAligners?: number
}

type PhotoItem = {
  id: string
  file: File
  previewUrl: string
}

interface AlignerCheckInModalProps {
  patientId: number
  alignerJourneyId?: number | null
  treatmentDetail?: any
  treatmentPlan?: AllTreatmentPlanListItem | null
  timelineAligners?: Aligner[]
  currentAlignerNo?: number
  onClose: (option: ActionItem) => void
  onSubmitted?: (submittedCheckIn: {
    alignerJourneyId: number
    alignerNo: number
  }) => Promise<void> | void
}

const FIT_OPTIONS = ['Perfect fit', 'Loose at the front', 'Not fitting at all', 'Loose at the back']

const FIT_VALUE_TO_CODE: Record<string, string> = {
  'Perfect fit': 'PERFECT_FIT',
  'Loose at the front': 'LOOSE_AT_FRONT',
  'Not fitting at all': 'NOT_FITTING',
  'Loose at the back': 'LOOSE_AT_BACK',
}

const MAX_PHOTOS_PER_GROUP = 5
const MAX_PHOTO_SIZE_BYTES = 15 * 1024 * 1024

const normalizeJawType = (jawType?: string | null): JawTypeLabel => {
  const normalizedJaw = String(jawType ?? '').toUpperCase()
  if (normalizedJaw === 'UPPER') return 'Upper'
  if (normalizedJaw === 'LOWER') return 'Lower'
  return 'Both'
}

const getJawTitle = (jawType: JawTypeLabel) => (jawType === 'Both' ? 'Upper & Lower' : jawType)

const getFileExtension = (file: File) => {
  const extensionFromName = file.name.split('.').pop()
  if (extensionFromName && extensionFromName !== file.name) {
    return extensionFromName.toLowerCase() === 'jpeg' ? 'jpg' : extensionFromName.toLowerCase()
  }

  const mimeExtension = file.type.split('/')[1]
  if (!mimeExtension) return 'jpg'
  return mimeExtension.toLowerCase() === 'jpeg' ? 'jpg' : mimeExtension.toLowerCase()
}

const createPhotoNameObjects = (photos: PhotoItem[]): IAlignerCheckInPhotoName[] =>
  photos.map((photo) => {
    const extension = getFileExtension(photo.file)
    return {
      original_filename: photo.file.name,
      save_as_filename: `${Date.now()}${generateRandomAlphanumeric(6)}.${extension}`,
    }
  })

const buildFeedbackForJaw = (fitValue: string) => ({
  fitting_feedback: {
    fittings: [FIT_VALUE_TO_CODE[fitValue]].filter(Boolean),
  },
  changing_feedback: {
    aligner_changing_issues: [],
  },
})

const getAlignerNumber = (aligner: any) =>
  safeParseInt(aligner?.aligner_number ?? aligner?.sr_no ?? aligner?.alignerNo)

const getAlignerJourneyId = (aligner: any) =>
  safeParseInt(
    aligner?.aligner_journey_id ??
      aligner?.journey_id ??
      aligner?.aligner_treatment_id ??
      aligner?.id
  )

const getTreatmentJourneyId = (treatmentDetail: any, alignerJourneyId?: number | null) =>
  safeParseInt(
    treatmentDetail?.aligner_journey_id ??
      treatmentDetail?.journey_id ??
      treatmentDetail?.aligner_treatment_id ??
      treatmentDetail?.id ??
      alignerJourneyId
  )

const getJawRange = (treatmentDetail: any, jawType: 'Upper' | 'Lower'): number[] => {
  const jaw = jawType === 'Upper' ? 'upper' : 'lower'
  const jawDetails =
    treatmentDetail?.[`${jaw}_jaw_details`] ??
    treatmentDetail?.aligner_details_meta_data?.[`${jaw}_jaw_details`] ??
    treatmentDetail?.aligner_details_meta_data?.[`${jaw}_jaw`] ??
    treatmentDetail?.aligner_treatment_details?.[`${jaw}_jaw`]
  const start = safeParseInt(jawDetails?.starts_with)
  const end = safeParseInt(jawDetails?.ends_with)

  if (start > 0 && end >= start) {
    return Array.from({length: end - start + 1}, (_, index) => start + index)
  }

  const range = treatmentDetail?.[`${jaw}_range`] ?? jawDetails?.range
  return Array.isArray(range)
    ? range.map((number) => safeParseInt(number)).filter((number) => number > 0)
    : []
}

const getPreviousAlignerNumbers = (
  upperRange: number[],
  lowerRange: number[],
  currentAlignerNo: number
) => {
  const rangeNumbers = [...upperRange, ...lowerRange]
  const lastKnownAlignerNo = Math.max(0, ...rangeNumbers)
  // Tracking ranges may stop before the current set; include every missing set in between.
  const numbers = [
    ...rangeNumbers,
    ...Array.from(
      {length: Math.max(0, currentAlignerNo - lastKnownAlignerNo)},
      (_, index) => lastKnownAlignerNo + index + 1
    ),
    currentAlignerNo,
  ]

  return Array.from(new Set(numbers))
    .filter((number) => number > 0 && number <= currentAlignerNo)
    .sort((first, second) => first - second)
}

const buildAlignerOptions = ({
  timelineAligners,
  treatmentDetail,
  treatmentPlan,
  alignerJourneyId,
  currentAlignerNo,
}: {
  timelineAligners?: Aligner[]
  treatmentDetail?: any
  treatmentPlan?: AllTreatmentPlanListItem | null
  alignerJourneyId?: number | null
  currentAlignerNo: number
}) => {
  const planAligners = Array.isArray(treatmentDetail?.aligners) ? treatmentDetail.aligners : []
  // Timeline rows contain the latest tracking state; they override plan rows for the same set.
  const rawAligners = [...planAligners, ...(timelineAligners ?? [])]

  const alignersToShow =
    currentAlignerNo > 0
      ? rawAligners.filter((aligner: any) => getAlignerNumber(aligner) <= currentAlignerNo)
      : rawAligners

  const optionsByNumber = new Map<number, AlignerCheckInOption>()
  const currentTimelineAligner = timelineAligners?.find(
    (aligner) => getAlignerNumber(aligner) === currentAlignerNo
  )
  const currentAlignerFromRows =
    currentTimelineAligner ??
    planAligners.find((aligner: any) => getAlignerNumber(aligner) === currentAlignerNo)
  const currentJawType = normalizeJawType(
    currentTimelineAligner?.jaw_type ??
      timelineAligners?.[0]?.current_aligner_jaw_type ??
      currentAlignerFromRows?.jaw_type ??
      currentAlignerFromRows?.current_aligner_jaw_type ??
      treatmentDetail?.current_aligner_jaw_type ??
      treatmentDetail?.jaw_type ??
      rawAligners[0]?.jaw_type
  )
  const currentAlignerJourneyId =
    getAlignerJourneyId(currentAlignerFromRows) ||
    getAlignerJourneyId(timelineAligners?.[0]) ||
    getTreatmentJourneyId(treatmentDetail, alignerJourneyId)
  const upperRange = getJawRange(treatmentPlan ?? treatmentDetail, 'Upper')
  const lowerRange = getJawRange(treatmentPlan ?? treatmentDetail, 'Lower')

  alignersToShow.forEach((aligner: any) => {
    const alignerNo = getAlignerNumber(aligner)
    if (!alignerNo) return

    const jawType = normalizeJawType(aligner?.jaw_type ?? aligner?.current_aligner_jaw_type)
    const resolvedJourneyId = getAlignerJourneyId(aligner) || currentAlignerJourneyId
    const key = `${jawType}-${alignerNo}`

    optionsByNumber.set(alignerNo, {
      key,
      label: `${getJawTitle(jawType)} ${alignerNo}`,
      alignerNo,
      jawType,
      alignerJourneyId: resolvedJourneyId || undefined,
      totalAligners: safeParseInt(aligner?.total_aligner ?? treatmentDetail?.total_aligners),
    })
  })

  if (currentAlignerNo > 0) {
    getPreviousAlignerNumbers(upperRange, lowerRange, currentAlignerNo).forEach((alignerNo) => {
      // Keep the recorded jaw type instead of adding the current jaw for the same set.
      if (optionsByNumber.has(alignerNo)) return

      const isUpper = upperRange.includes(alignerNo)
      const isLower = lowerRange.includes(alignerNo)
      const jawType =
        isUpper && isLower ? 'Both' : isUpper ? 'Upper' : isLower ? 'Lower' : currentJawType
      const key = `${jawType}-${alignerNo}`

      optionsByNumber.set(alignerNo, {
        key,
        label: `${getJawTitle(jawType)} ${alignerNo}`,
        alignerNo,
        jawType,
        alignerJourneyId: currentAlignerJourneyId || undefined,
        totalAligners: safeParseInt(
          currentAlignerFromRows?.total_aligner ?? treatmentDetail?.total_aligners
        ),
      })
    })
  }

  return Array.from(optionsByNumber.values()).sort(
    (first, second) => first.alignerNo - second.alignerNo
  )
}

const AlignerCheckInModal = ({
  patientId,
  alignerJourneyId,
  treatmentDetail,
  treatmentPlan,
  timelineAligners,
  currentAlignerNo: trackingCurrentAlignerNo,
  onClose,
  onSubmitted,
}: AlignerCheckInModalProps) => {
  const {dispatchAction} = useDispatchAction()
  const [selectedAlignerKey, setSelectedAlignerKey] = useState('')
  const [selectedFitUpper, setSelectedFitUpper] = useState('')
  const [selectedFitLower, setSelectedFitLower] = useState('')
  const [feedbackText, setFeedbackText] = useState('')
  const [withAlignerPhotos, setWithAlignerPhotos] = useState<PhotoItem[]>([])
  const [withoutAlignerPhotos, setWithoutAlignerPhotos] = useState<PhotoItem[]>([])
  const [photoError, setPhotoError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const withAlignerPhotosRef = useRef<PhotoItem[]>([])
  const withoutAlignerPhotosRef = useRef<PhotoItem[]>([])

  const currentAlignerNo =
    safeParseInt(trackingCurrentAlignerNo) ||
    safeParseInt(
      timelineAligners?.find((aligner) => safeParseInt(aligner.current_aligner_number) > 0)
        ?.current_aligner_number
    ) ||
    safeParseInt(treatmentDetail?.current_aligner_no) ||
    getAlignerNumber(timelineAligners?.[0])

  const alignerOptions = useMemo(
    () =>
      buildAlignerOptions({
        timelineAligners,
        treatmentDetail,
        treatmentPlan,
        alignerJourneyId,
        currentAlignerNo,
      }),
    [alignerJourneyId, timelineAligners, treatmentDetail, treatmentPlan, currentAlignerNo]
  )

  const defaultAlignerOption = useMemo(() => {
    return (
      alignerOptions.find((option) => option.alignerNo === currentAlignerNo) ??
      alignerOptions[alignerOptions.length - 1] ??
      null
    )
  }, [alignerOptions, currentAlignerNo])

  const selectedAligner =
    alignerOptions.find((option) => option.key === selectedAlignerKey) ?? defaultAlignerOption

  const isUpperVisible = selectedAligner?.jawType === 'Upper' || selectedAligner?.jawType === 'Both'
  const isLowerVisible = selectedAligner?.jawType === 'Lower' || selectedAligner?.jawType === 'Both'
  const isFitIncomplete =
    (isUpperVisible && !selectedFitUpper) || (isLowerVisible && !selectedFitLower)

  useEffect(() => {
    withAlignerPhotosRef.current = withAlignerPhotos
  }, [withAlignerPhotos])

  useEffect(() => {
    withoutAlignerPhotosRef.current = withoutAlignerPhotos
  }, [withoutAlignerPhotos])

  useEffect(() => {
    return () => {
      withAlignerPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
      withoutAlignerPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
    }
  }, [])

  const clearPhotos = () => {
    withAlignerPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
    withoutAlignerPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
    setWithAlignerPhotos([])
    setWithoutAlignerPhotos([])
  }

  const resetForm = () => {
    setSelectedAlignerKey('')
    setSelectedFitUpper('')
    setSelectedFitLower('')
    setFeedbackText('')
    setPhotoError('')
    clearPhotos()
  }

  const handleClose = () => {
    resetForm()
    onClose(actionTypes.ALIGNER_CHECK_IN)
  }

  const addPhotos = (
    event: ChangeEvent<HTMLInputElement>,
    currentPhotos: PhotoItem[],
    setPhotos: (photos: PhotoItem[]) => void
  ) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''

    if (files.length === 0) return

    const nextPhotos = [...currentPhotos]
    let nextError = ''

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        nextError = 'Only image files can be uploaded.'
        return
      }

      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        nextError = 'Please upload photos with a maximum size of 15 MB.'
        return
      }

      if (nextPhotos.length >= MAX_PHOTOS_PER_GROUP) {
        nextError = `You can upload up to ${MAX_PHOTOS_PER_GROUP} photos in each section.`
        return
      }

      nextPhotos.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })
    })

    setPhotoError(nextError)
    setPhotos(nextPhotos)
  }

  const removePhoto = (
    photoId: string,
    currentPhotos: PhotoItem[],
    setPhotos: (photos: PhotoItem[]) => void
  ) => {
    const photoToRemove = currentPhotos.find((photo) => photo.id === photoId)
    if (photoToRemove) {
      URL.revokeObjectURL(photoToRemove.previewUrl)
    }
    setPhotos(currentPhotos.filter((photo) => photo.id !== photoId))
    setPhotoError('')
  }

  const handleAlignerChange = (nextAlignerKey: string) => {
    setSelectedAlignerKey(nextAlignerKey)
    setSelectedFitUpper('')
    setSelectedFitLower('')
  }

  const buildFeedback = () => {
    const feedbacks: ApiAlignerCheckInPayload['details']['feedback']['feedbacks'] = {}

    if (isUpperVisible) {
      feedbacks.UPPER = buildFeedbackForJaw(selectedFitUpper)
    }

    if (isLowerVisible) {
      feedbacks.LOWER = buildFeedbackForJaw(selectedFitLower)
    }

    return {
      feedbacks,
      other_issues: feedbackText.trim(),
    }
  }

  const handleSubmit = async () => {
    if (submitting) return
    if (!selectedAligner) {
      ErrorToast('Please select an aligner set.')
      return
    }

    if (isFitIncomplete) {
      ErrorToast('Please select fit feedback for the visible jaw.')
      return
    }

    const selectedJourneyId =
      selectedAligner.alignerJourneyId ||
      safeParseInt(alignerJourneyId) ||
      safeParseInt(treatmentDetail?.journey_id)

    if (!selectedJourneyId) {
      ErrorToast('Unable to find aligner journey details.')
      return
    }

    const payload: ApiAlignerCheckInPayload = {
      details: {
        user_id: safeParseInt(patientId),
        user_type: 'PATIENT',
        aligner_journey_id: selectedJourneyId,
        aligner_no: selectedAligner.alignerNo,
        feedback: buildFeedback(),
        without_aligner_photo_files: createPhotoNameObjects(withoutAlignerPhotos),
        with_aligner_photo_files: createPhotoNameObjects(withAlignerPhotos),
      },
      photo: [
        ...withAlignerPhotos.map((photo) => photo.file),
        ...withoutAlignerPhotos.map((photo) => photo.file),
      ],
    }

    setSubmitting(true)
    try {
      const responseStatus = await dispatchAction(postAlignerCheckIn(payload)).unwrap()

      if (responseStatus === 'SP0004') {
        ErrorToast('Storage limit exceeded.')
        return
      }

      if (responseStatus !== 200 && responseStatus !== '200') {
        ErrorToast('Check-in failed. Please try again.')
        return
      }

      await onSubmitted?.({
        alignerJourneyId: selectedJourneyId,
        alignerNo: selectedAligner.alignerNo,
      })
      SuccessToast('Check-in completed successfully!')
      resetForm()
      onClose(actionTypes.ALIGNER_CHECK_IN)
    } catch (error: any) {
      if (error === 'SP0004') {
        ErrorToast('Storage limit exceeded.')
        return
      }

      ErrorToast(
        typeof error === 'string' ? error : (error?.message ?? 'Check-in failed. Please try again.')
      )
    } finally {
      setSubmitting(false)
    }
  }

  const renderFitSection = (
    title: string,
    selectedValue: string,
    onSelect: (value: string) => void
  ) => (
    <div className='mb-6'>
      <div className='mb-3 inline-flex min-w-[125px] items-center justify-center rounded-full bg-secondaryColor px-5 py-3 text-base font-bold text-white'>
        {title}
      </div>
      <div className='flex flex-wrap gap-3'>
        {FIT_OPTIONS.map((option) => {
          const isActive = selectedValue === option
          return (
            <button
              key={`${title}-${option}`}
              type='button'
              className={cn(
                'rounded-full border px-5 py-3 text-sm font-medium transition-colors',
                isActive
                  ? 'border-secondaryColor bg-secondarySupport text-secondaryColor font-bold'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-secondaryColor'
              )}
              onClick={() => onSelect(option)}
            >
              {option}
            </button>
          )
        })}
      </div>
    </div>
  )

  const renderPhotoUpload = (
    title: string,
    photos: PhotoItem[],
    setPhotos: (photos: PhotoItem[]) => void,
    inputId: string
  ) => {
    const uploadDisabled = photos.length >= MAX_PHOTOS_PER_GROUP
    return (
      <div>
        <div className='mb-2 font-bold text-gray-700'>{title}</div>
        {photos.length > 0 && (
          <div className='mb-4 flex flex-wrap gap-2'>
            {photos.map((photo) => (
              <div
                key={photo.id}
                className='relative h-[70px] w-[70px] overflow-hidden rounded-lg border border-gray-300'
              >
                <img
                  src={photo.previewUrl}
                  alt={photo.file.name}
                  className='h-full w-full object-cover'
                />
                <button
                  type='button'
                  aria-label='Remove photo'
                  className='absolute right-1 top-1 rounded-full bg-white p-1 text-gray-600 shadow hover:text-red-500'
                  onClick={() => removePhoto(photo.id, photos, setPhotos)}
                >
                  <Trash2 className='h-3 w-3' />
                </button>
              </div>
            ))}
          </div>
        )}
        <label
          htmlFor={inputId}
          className={cn(
            'mb-6 flex h-36 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-colors',
            uploadDisabled
              ? 'cursor-not-allowed border-textColor text-textColor'
              : 'border-primaryColor text-primaryColor hover:bg-primarySupport'
          )}
        >
          <UploadCloud className='mb-2 h-8 w-8' />
          <span className='text-sm font-semibold'>Upload photo</span>
        </label>
        <input
          id={inputId}
          type='file'
          accept='image/*'
          multiple
          disabled={uploadDisabled}
          className='hidden'
          onChange={(event) => addPhotos(event, photos, setPhotos)}
        />
      </div>
    )
  }

  return (
    <ModalLayout isResponsive className='md:!w-[48rem] max-h-[95vh] overflow-hidden !p-0'>
      <div className='flex max-h-[95vh] flex-col'>
        <div className='flex items-start justify-between border-b border-gray-200 px-6 py-5'>
          <div>
            <div className='flex items-center gap-2'>
              <ClipboardCheck className='h-6 w-6 text-primaryColor' />
              <h2 className='text-2xl font-bold text-black'>Aligner check-in</h2>
            </div>
            <p className='mt-2 text-base font-medium text-gray-500'>
              Add feedback and images to complete check-in.
            </p>
          </div>
          <button
            type='button'
            aria-label='Close aligner check-in'
            className='rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-black'
            onClick={handleClose}
            disabled={submitting}
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-5'>
          <div>
            <label className='mb-2 block text-base font-bold text-black'>Select aligner set</label>
            <Select
              value={selectedAligner?.key}
              className='w-full'
              size='large'
              placeholder='Select aligner set'
              getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
              options={alignerOptions.map((option) => ({
                value: option.key,
                label: `${getJawTitle(option.jawType)} ${option.alignerNo}`,
              }))}
              onChange={handleAlignerChange}
              disabled={alignerOptions.length === 0 || submitting}
            />
          </div>

          <div className='mt-6'>
            <h3 className='mb-3 text-base font-bold text-black'>How is the fit?</h3>
            {isUpperVisible && renderFitSection('Upper Jaw', selectedFitUpper, setSelectedFitUpper)}
            {isLowerVisible && renderFitSection('Lower Jaw', selectedFitLower, setSelectedFitLower)}
          </div>

          <div className='mt-2'>
            <label className='mb-2 block text-base font-bold text-black'>
              Any other issues? (Optional)
            </label>
            <textarea
              className='h-32 w-full resize-none rounded-xl border border-gray-300 bg-white p-4 text-base text-black outline-none focus:border-primaryColor'
              placeholder='Describe any issues...'
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              disabled={submitting}
            />
          </div>

          <div className='mt-6'>
            <div className='mb-2 flex items-center gap-2'>
              <ImagePlus className='h-5 w-5 text-primaryColor' />
              <h3 className='text-lg font-bold text-black'>Upload photos</h3>
            </div>
            <div className='mb-4 flex items-center text-sm text-gray-500'>
              <span>You can upload up to 5 photos for each jaw.</span>
            </div>
            {photoError && <p className='mb-3 text-sm text-red-500'>{photoError}</p>}
            {renderPhotoUpload(
              'With aligner',
              withAlignerPhotos,
              setWithAlignerPhotos,
              'aligner-check-in-with-aligner'
            )}
            {renderPhotoUpload(
              'Without aligner',
              withoutAlignerPhotos,
              setWithoutAlignerPhotos,
              'aligner-check-in-without-aligner'
            )}
          </div>
        </div>

        <div className='border-t border-gray-200 bg-white px-6 py-4'>
          <button
            type='button'
            className='flex h-14 w-full items-center justify-center rounded-xl bg-primaryColor text-lg font-bold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50'
            onClick={() => void handleSubmit()}
            disabled={submitting || isFitIncomplete || !selectedAligner}
          >
            {submitting ? <Spinner loading size={20} color='#ffffff' /> : 'Check-in Aligner'}
          </button>
        </div>
      </div>
    </ModalLayout>
  )
}

export default AlignerCheckInModal
